import React, { useState, useEffect } from "react";
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useTheme,
    useMediaQuery,
    Typography,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Tooltip, Chip } from "@mui/material";

function Navbar() {
    const { logout, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [searchValue, setSearchValue] = useState("");
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);


    useEffect(() => {
        const handlePageSearch = (e) => {
            if (e.detail !== undefined) {
                setSearchValue(e.detail);
            }
        };
        window.addEventListener('tech-page-search', handlePageSearch);
        return () => window.removeEventListener('tech-page-search', handlePageSearch);
    }, []);

    const handleSearchChange = (val) => {
        setSearchValue(val);
        window.dispatchEvent(new CustomEvent('header-search', { detail: val }));
    };

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const openLogoutDialog = () => {
        setLogoutDialogOpen(true);
    };

    const closeLogoutDialog = () => {
        setLogoutDialogOpen(false);
    };

    const confirmLogout = () => {
        closeLogoutDialog();
        handleLogout();
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleCopyCompanyId = (id) => {
        navigator.clipboard.writeText(id);
        // Optional: show a small toast or change tooltip text
    };

    const getDashboardPath = () => {
        if (!user) return "/";
        if (user.role === "super_admin") return "/super-admin/dashboard";
        if (user.role === "admin") return "/admin/dashboard";
        return "/candidate/dashboard";
    };

    const baseMenuItems = [
        { text: "Home", path: "/" },
        { text: "About", path: "/about" },
        { text: "Pricing", path: "/pricing" },
        { text: "Jobs", path: "/jobs" },
        { text: "Contact", path: "/contact" },
        { text: "Profile", path: "/profile" },
    ];

    // ✅ UPDATED LOGIC: Hide 'Pricing' for candidates, 'Jobs' for recruiters (admin)
    // AND Handle contextual Dashboard visibility
    const menuItems = [...baseMenuItems].filter(item => {
        if (!isAuthenticated) {
            // Hide Jobs and Profile before login
            return item.text !== "Jobs" && item.text !== "Profile";
        }

        // Hide Pricing for candidates after login
        if (user?.role === "candidate" && item.text === "Pricing") {
            return false;
        }

        // Hide Jobs for recruiters (admin) after login
        if (user?.role === "admin" && item.text === "Jobs") {
            return false;
        }

        // Hide Home and About for logged-in users to match required header structure
        if (item.text === "Home" || item.text === "About") {
            return false;
        }

        // Show Pricing, Contact, Jobs, and Profile after login (if not filtered above)
        return item.text === "Pricing" || item.text === "Contact" || item.text === "Jobs" || item.text === "Profile";
    });

    if (isAuthenticated) {
        const dashboardPath = getDashboardPath();
        if (location.pathname !== dashboardPath) {
            menuItems.unshift({ text: "Dashboard", path: dashboardPath });
        }
    }

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
            <Box 
                component={RouterLink} 
                to="/" 
                sx={{ py: 2, display: "flex", justifyContent: "center", cursor: "pointer", textDecoration: 'none' }}
            >
                <img src="/logo-new.png?v=3" alt="ExiHire Logo" style={{ height: 45 }} />
            </Box>

            <List>
                {menuItems.map((item) => (
                    <ListItem
                        key={item.text}
                        component={RouterLink}
                        to={item.path}
                        sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5 }}
                    >
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}

                {isAuthenticated ? (
                    <>

                        <ListItem onClick={openLogoutDialog} sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5, cursor: "pointer" }}>
                            <ListItemText primary="Logout" />
                        </ListItem>
                    </>
                ) : (
                    <ListItem component={RouterLink} to="/login" sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5 }}>
                        <ListItemText primary="Login" />
                    </ListItem>
                )}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, bgcolor: '#0A0F2C', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: "none" } }}>
                            <MenuIcon />
                        </IconButton>

                        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'white', cursor: 'pointer' }}>
                            <img src="/logo-new.png?v=3" alt="ExiHire Logo" style={{height: isMobile ? 45 : 60, marginRight: 13, transition: 'height 0.3s ease' }} />
                        </Box>
                    </Box>

                    {location.pathname === '/admin/technologies' && (
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', mr: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Search..."
                                value={searchValue}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                sx={{
                                    width: { xs: '120px', sm: '200px', md: '300px' },
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        borderRadius: '20px',
                                        height: '40px',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'white',
                                        },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        opacity: 1,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: 'center' }}>
                        {menuItems.map((item) => (
                            <Button key={item.text} color="inherit" component={RouterLink} to={item.path} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}>
                                {item.text}
                            </Button>
                        ))}

                        {isAuthenticated && user?.formatted_company_id && (
                            <Tooltip title="Copy Company ID" arrow>
                                <Chip
                                    label={`ID: ${user.formatted_company_id}`}
                                    onClick={() => handleCopyCompanyId(user.formatted_company_id)}
                                    icon={<ContentCopyIcon sx={{ fontSize: '14px !important', color: 'rgba(255,255,255,0.7) !important' }} />}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        borderRadius: '6px',
                                        height: '32px',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.15)',
                                            borderColor: 'rgba(255,255,255,0.3)',
                                        },
                                        '& .MuiChip-label': {
                                            px: 1,
                                        },
                                        '& .MuiChip-icon': {
                                            ml: 0.5,
                                        }
                                    }}
                                />
                            </Tooltip>
                        )}

                        {isAuthenticated ? (
                            <Button
                                color="inherit"
                                onClick={openLogoutDialog}
                                startIcon={<LogoutIcon />}
                                sx={{
                                    border: '1px solid rgba(255,255,255,0.5)',
                                    borderRadius: '8px',
                                    px: 2,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        borderColor: 'white'
                                    }
                                }}
                            >
                                Logout
                            </Button>
                        ) : (
                            <Button
                                sx={{ backgroundColor: "white", color: 'primary.main', borderRadius: '8px', px: 3, textTransform: 'none', fontWeight: 600, ":hover": { backgroundColor: "#f0ffff" } }}
                                component={RouterLink}
                                to="/login"
                            >
                                Login
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="nav">
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 } }}
                >
                    {drawer}
                </Drawer>
            </Box>
            <Dialog
                open={logoutDialogOpen}
                onClose={closeLogoutDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Confirm Logout
                </DialogTitle>

                <DialogContent>
                    Are you sure you want to logout?
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={closeLogoutDialog}
                        variant="outlined"
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={confirmLogout}
                        variant="contained"
                        color="error"
                    >
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Navbar;
